import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Crown, Loader2, Sparkles, Brain, Lightbulb, TrendingUp, Check, AlertCircle } from 'lucide-react';
import { ThemeSelector } from './ThemeSelector';
import { PremiumModuleToggle } from './PremiumModuleToggle';
import { PremiumLockedOverlay } from './PremiumLockedOverlay';
import { PremiumLoadingOverlay } from './PremiumLoadingOverlay';
import { PremiumComingSoonBlock } from './PremiumComingSoonBlock';
import { FeatureSuggestionModal } from './FeatureSuggestionModal';
import { DeliveryTimeSelector } from './DeliveryTimeSelector';
import { NewsletterTextarea } from '@/components/NewsletterTextarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackEventMp } from '@/components/Mixpanel';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PremiumSettings {
  theme: 'light_professional' | 'dark_legacy';
  modules: {
    dailyReflection: boolean;
    dailyCuriosity: boolean;
    businessTip: boolean;
    githubTrending: boolean;
  };
  timezone: string;
  preferredDeliveryHour: number;
}

interface PremiumSettingsCardProps {
  userData: any;
  sessionToken: string;
  subscriptionStatus: any;
  isLoadingSubscription?: boolean;
  initialSettings: PremiumSettings;
  interests: string;
  onInterestsChange: (interests: string) => void;
  onSettingsUpdate?: (settings: PremiumSettings) => void;
}

export const PremiumSettingsCard = ({
  userData,
  sessionToken,
  subscriptionStatus,
  isLoadingSubscription = false,
  initialSettings,
  interests,
  onInterestsChange,
  onSettingsUpdate
}: PremiumSettingsCardProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [settings, setSettings] = useState<PremiumSettings>(initialSettings);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);

  // Sync internal state when initialSettings changes (e.g., after async data load from magic link)
  useEffect(() => {
    if (initialSettings.timezone && initialSettings.timezone !== settings.timezone) {
      console.log('[PremiumSettingsCard] Syncing initialSettings - timezone changed:', {
        old: settings.timezone,
        new: initialSettings.timezone
      });
      setSettings(initialSettings);
    }
  }, [initialSettings.timezone, initialSettings.theme, initialSettings.preferredDeliveryHour]);

  // Debounce para auto-save de interesses (2 segundos)
  useEffect(() => {
    if (isLocked) return;
    
    // Limpar timeout anterior
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    // Não salvar se vazio ou muito curto
    if (!interests || interests.length < 70) {
      return;
    }
    
    // Criar novo timeout de 2 segundos
    const timeout = setTimeout(() => {
      autoSave(false);
    }, 2000);
    
    setAutoSaveTimeout(timeout);
    
    // Cleanup
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [interests]);

  // Helper para lidar com ambos formatos de status: subscription_status (check-subscription) ou status (get-preferences)
  const getStatusValue = (): string | null => {
    if (!subscriptionStatus) return null;
    return subscriptionStatus.subscription_status ?? subscriptionStatus.status ?? null;
  };

  const isPremiumActive = (): boolean => {
    const status = getStatusValue();
    if (!status) return false;
    
    const isActive = status === 'active';
    const isTrialActive = status === 'trial' && 
                          subscriptionStatus.trial_end_date && 
                          new Date(subscriptionStatus.trial_end_date) > new Date();
    
    return isActive || isTrialActive;
  };

  const isLocked = !isPremiumActive();

  const handleReactivate = () => {
    const status = getStatusValue();
    trackEventMp('premium_reactivate_clicked', {
      source: 'preferences_premium_section',
      subscription_status: status || 'unknown'
    });
    
    // Navigate to subscription page or customer portal
    window.location.href = '/subscribe';
  };

  const autoSave = async (immediate: boolean = false, overrideSettings?: Partial<PremiumSettings>) => {
    if (isLocked) return;
    
    // Usar overrideSettings se fornecido, senão usa settings atual
    const currentSettings = overrideSettings 
      ? { ...settings, ...overrideSettings }
      : settings;
    
    // 🔒 CRITICAL: Prevent saving if timezone is not loaded yet (prevents race condition with crawlers)
    if (!currentSettings.timezone) {
      console.warn('[AUTO-SAVE] Aborted - timezone not loaded from database yet, preventing default value save');
      return;
    }
    
    // Validação de interesses
    if (!interests || interests.length < 70 || interests.length > 500) {
      setSaveStatus('error');
      toast({
        title: t('preferences.errors.errorTitle'),
        description: t('preferences.errors.interestsLength'),
        variant: 'destructive'
      });
      return;
    }
    
    setSaveStatus('saving');
    
    try {
      const { data, error } = await supabase.functions.invoke('update-preferences', {
        body: {
          session_token: sessionToken,
          interests: interests.trim(),
          newsletter_theme: currentSettings.theme,
          module_daily_reflection: currentSettings.modules.dailyReflection,
          module_daily_curiosity: currentSettings.modules.dailyCuriosity,
          module_business_tip: currentSettings.modules.businessTip,
          module_github_trending: currentSettings.modules.githubTrending,
          timezone: currentSettings.timezone,
          preferred_delivery_hour: currentSettings.preferredDeliveryHour
        }
      });

      if (error) throw error;

      if (data?.success) {
        setSaveStatus('saved');
        setLastSavedAt(new Date());
        
        // Toast discreto apenas se ação imediata (tema/módulo)
        if (immediate) {
          toast({
            title: t('preferences.premium.saved'),
            duration: 2000
          });
        }
        
        onSettingsUpdate?.(currentSettings);
        
        // Reset status após 3 segundos
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error: any) {
      console.error('Error auto-saving:', error);
      setSaveStatus('error');
      toast({
        title: t('preferences.errors.errorTitle'),
        description: t('preferences.errors.updateError'),
        variant: 'destructive'
      });
    }
  };

  const handleThemeChange = (theme: 'light_professional' | 'dark_legacy') => {
    const oldTheme = settings.theme;
    setSettings(prev => ({ ...prev, theme }));
    
    const status = getStatusValue();
    trackEventMp('premium_theme_changed', {
      from: oldTheme,
      to: theme,
      subscription_status: status || 'unknown'
    });
    
    // Auto-save imediato passando novo valor diretamente
    setTimeout(() => autoSave(true, { theme }), 100);
  };

  const handleModuleToggle = (module: keyof PremiumSettings['modules'], enabled: boolean) => {
    const newModules = { ...settings.modules, [module]: enabled };
    
    setSettings(prev => ({
      ...prev,
      modules: newModules
    }));

    const status = getStatusValue();
    trackEventMp('premium_module_toggled', {
      module,
      enabled,
      subscription_status: status || 'unknown'
    });
    
    // Auto-save imediato passando novos valores diretamente
    setTimeout(() => autoSave(true, { modules: newModules }), 100);
  };

  const handleTimezoneChange = (timezone: string) => {
    setSettings(prev => ({ ...prev, timezone }));
    
    const status = getStatusValue();
    trackEventMp('delivery_timezone_changed', {
      timezone,
      subscription_status: status || 'unknown'
    });
    
    // Auto-save imediato
    setTimeout(() => autoSave(true, { timezone }), 100);
  };

  const handleDeliveryHourChange = (hour: number) => {
    setSettings(prev => ({ ...prev, preferredDeliveryHour: hour }));
    
    const status = getStatusValue();
    trackEventMp('delivery_hour_changed', {
      hour,
      subscription_status: status || 'unknown'
    });
    
    // Auto-save imediato
    setTimeout(() => autoSave(true, { preferredDeliveryHour: hour }), 100);
  };

  const modules = [
    {
      key: 'dailyReflection' as const,
      icon: <Brain className="h-5 w-5" />,
      title: t('preferences.premium.modules.reflection.title'),
      description: t('preferences.premium.modules.reflection.description')
    },
    {
      key: 'dailyCuriosity' as const,
      icon: <Lightbulb className="h-5 w-5" />,
      title: t('preferences.premium.modules.curiosity.title'),
      description: t('preferences.premium.modules.curiosity.description')
    },
    {
      key: 'businessTip' as const,
      icon: <TrendingUp className="h-5 w-5" />,
      title: t('preferences.premium.modules.business.title'),
      description: t('preferences.premium.modules.business.description')
    },
    {
      key: 'githubTrending' as const,
      icon: <Sparkles className="h-5 w-5" />,
      title: t('preferences.premium.modules.github.title'),
      description: t('preferences.premium.modules.github.description')
    }
  ];

  return (
    <div className="relative overflow-hidden rounded-lg border border-primary/30 
      bg-gradient-to-b from-primary/10 via-background to-background
      shadow-[0_0_30px_rgba(139,92,246,0.2),inset_0_0_30px_rgba(139,92,246,0.03)]
      backdrop-blur-sm animate-pulse-highlight">
      <CardHeader className="relative">
        {/* Background gradient overlay for header */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 opacity-50 rounded-t-lg" />
        
        <div className="relative flex items-center gap-3">
          {/* Crown with glow effect */}
          <div className="relative">
            <Crown className="h-6 w-6 text-primary drop-shadow-[0_0_8px_rgba(139,92,246,0.6)]" />
            {/* Animated glow behind crown */}
            <div className="absolute inset-0 blur-xl bg-primary/30 animate-pulse-slow -z-10" />
          </div>
          
          <CardTitle className="bg-gradient-to-r from-primary via-purple-400 to-primary bg-clip-text text-transparent">
            {t('preferences.premium.title')}
          </CardTitle>
        </div>
        
        {/* Decorative gradient line */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/50 to-transparent mt-4" />
        
        <CardDescription className="mt-3">
          {t('preferences.premium.sectionDescription')}
        </CardDescription>
        {saveStatus !== 'idle' && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
            {saveStatus === 'saving' && (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{t('preferences.premium.saving')}</span>
              </>
            )}
            {saveStatus === 'saved' && lastSavedAt && (
              <>
                <Check className="h-3 w-3 text-green-600" />
                <span className="text-green-600">
                  {t('preferences.premium.savedAt', { 
                    time: formatDistanceToNow(lastSavedAt, { addSuffix: true, locale: ptBR }) 
                  })}
                </span>
              </>
            )}
            {saveStatus === 'error' && (
              <>
                <AlertCircle className="h-3 w-3 text-destructive" />
                <span className="text-destructive">{t('preferences.premium.saveError')}</span>
              </>
            )}
          </div>
        )}
      </CardHeader>
      
      <CardContent className="space-y-6 relative">
        {/* Shimmer effect subtle in background */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent 
          animate-shimmer opacity-30 pointer-events-none" />
        
        <div className="relative z-10 space-y-6">
          {/* Theme Selector with premium container */}
          <div className="p-4 rounded-lg bg-background/50 backdrop-blur-sm border border-primary/10
            hover:border-primary/30 transition-all duration-300">
            <ThemeSelector
              value={settings.theme}
              onChange={handleThemeChange}
              disabled={isLocked}
            />
          </div>

          {/* Delivery Time Settings - Timezone & Preferred Hour */}
          <DeliveryTimeSelector
            timezone={settings.timezone}
            deliveryHour={settings.preferredDeliveryHour}
            onTimezoneChange={handleTimezoneChange}
            onDeliveryHourChange={handleDeliveryHourChange}
            disabled={isLocked}
          />

          {/* Interests with premium styling */}
          <div className="space-y-3 p-4 rounded-lg bg-background/50 backdrop-blur-sm border border-primary/10
            hover:border-primary/30 transition-all duration-300">
            <div>
              <Label htmlFor="interests" className="text-lg font-semibold">
                {t('preferences.interestsLabel')}
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                {t('preferences.interestsDescription')}
              </p>
            </div>
            <NewsletterTextarea
              value={interests}
              onChange={onInterestsChange}
              showQuickFills={false}
            />
          </div>

          {/* Premium Modules */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary animate-pulse" />
              <h3 className="text-lg font-semibold text-foreground">
                {t('preferences.premium.modules.title')}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('preferences.premium.modules.subtitle')}
            </p>
            
            <div className="space-y-2">
              {modules.map((module) => (
                <PremiumModuleToggle
                  key={module.key}
                  icon={module.icon}
                  title={module.title}
                  description={module.description}
                  enabled={settings.modules[module.key]}
                  onToggle={(enabled) => handleModuleToggle(module.key, enabled)}
                  disabled={isLocked}
                />
              ))}
            </div>
          </div>

          {/* Coming Soon Block - FOMO */}
          <PremiumComingSoonBlock
            onSuggestClick={() => {
              setSuggestionModalOpen(true);
              trackEventMp('premium_suggestion_modal_opened', {
                subscription_status: getStatusValue() || 'unknown'
              });
            }}
          />
        </div>

      </CardContent>

      {/* Feature Suggestion Modal */}
      <FeatureSuggestionModal
        open={suggestionModalOpen}
        onOpenChange={setSuggestionModalOpen}
        userData={userData}
        subscriptionStatus={subscriptionStatus}
      />

      {/* Loading or Locked Overlay */}
      {isLoadingSubscription ? (
        <PremiumLoadingOverlay />
      ) : (
        isLocked && <PremiumLockedOverlay onReactivate={handleReactivate} />
      )}
    </div>
  );
};